import z from 'zod'
import {
  firestoreWriteTimestamp,
  nullSafeFirestoreTimestamp,
  nullSafeOptionalBoolean,
  nullSafeOptionalNumber,
  nullSafeOptionalFirestoreTimestamp,
  nullSafeOptionalString,
  nullSafeStringArray,
} from '../shared/schema'
import { RoomStateVideoMethod } from '../RoomState/types'

export enum SectionState {
  unconfigured = -1,
  notStarted = 0,
  inProgress = 1,
  completed = 2,
}

export enum SectionPaymentMethod {
  token = 0,
  sectionPassStripe = 1,
  sectionPassCoupon = 2,
  sectionPassStripeCoupon = 3,
  sectionPassCodelessAccess = 4,
}

export const schema = z.object({
  className: z.string(),
  instructorUserId: z.string(),
  collaboratorRWUserIds: nullSafeStringArray([]),
  invoiced: nullSafeOptionalBoolean(false),
  canvasCourseId: nullSafeOptionalString(),
  ltiCourseId: nullSafeOptionalString(),
  sectionName: z.string(),
  sectionState: z.preprocess((val) => {
    return val ?? SectionState.notStarted
  }, z.nativeEnum(SectionState)),
  sectionPaymentMethod: z.nativeEnum(SectionPaymentMethod).optional(),
  expectedStudents: nullSafeOptionalNumber(),
  /**
   * @deprecated call `isReadOnlyShareableSectionForCurrentUser` on Section model instead
   */
  shareable: nullSafeOptionalBoolean(false),
  removedUserIds: nullSafeStringArray([]).optional(),
  userIds: nullSafeStringArray([]),
  sectionPassPrice: nullSafeOptionalNumber(),
  sectionPassQuantity: nullSafeOptionalNumber(),
  organizationId: nullSafeOptionalString(),
  updatedAt: nullSafeFirestoreTimestamp(),
  videoMethod: z
    .preprocess(
      (val) => (val as RoomStateVideoMethod) ?? RoomStateVideoMethod.broadcast,
      z.nativeEnum(RoomStateVideoMethod)
    )
    .optional(),
  hubspotDealId: nullSafeOptionalString(),
  sectionDealExpiresAt: nullSafeOptionalFirestoreTimestamp(),
  sectionStartDate: nullSafeOptionalFirestoreTimestamp(),
  sectionEndDate: nullSafeOptionalFirestoreTimestamp(),
  // undefined = opt-out list never uploaded, [] = uploaded but no opt-outs,
  // [...ids] = uploaded with opt-outs.
  optOutUserIds: z.array(z.string()).optional(),
  isTest: nullSafeOptionalBoolean(false),
})

export const writeSchema = schema.extend({
  updatedAt: firestoreWriteTimestamp,
})

export const empty = Object.freeze(
  schema.parse({
    instructorUserId: '',
    collaboratorRWUserIds: [],
    className: '',
    sectionName: '',
    updatedAt: { seconds: 0, nanoseconds: 0 },
    userIds: [],
  })
)

export type FirestoreSectionInferredType = z.infer<typeof schema>

export interface FirestoreSection extends FirestoreSectionInferredType {}
